import { DepositForAccount } from "@mezo/api"
import {
  MIGRATABLE_TOKENS,
  STBTC_TOKENS,
  StbtcTokens,
  SUPERSTACK_TOKENS,
} from "@mezo/sdk"
import { createSelector } from "@reduxjs/toolkit"

import { FRIENDLY_DECIMALS_AMOUNT } from "#/shared/constants"
import {
  bigIntToFloat,
  getTotalDepositsAmount,
  isTokenEligibleForUnlocks,
} from "#/shared/utils"

import { RootState } from "../reducers"

import { selectCanSendTransactions } from "./ui"
import {
  selectIsConnected,
  selectIsOnEthereumChain,
  selectIsOnMezoChain,
} from "./wallet"

export type DepositWithWithdrawalStatus = DepositForAccount & {
  withdrawn: boolean
  redemptionNeeded: boolean
}

export const selectDeposits = (state: RootState) => state.portal.deposits

export const selectWithdrawals = (state: RootState) => state.portal.withdrawals

export const selectDepositsWithWithdrawalStatus = createSelector(
  selectDeposits,
  selectWithdrawals,
  (deposits, withdrawals) => {
    const withdrawalsMap = new Map(
      withdrawals.map((withdrawal) => [withdrawal.depositId, withdrawal]),
    )

    return deposits.map<DepositWithWithdrawalStatus>((deposit) => {
      const { depositId, token } = deposit
      const withdrawal = withdrawalsMap.get(depositId)

      const redemptionNeeded =
        withdrawal && token === "BTC"
          ? !withdrawal.redemptionRequested && !withdrawal.redemptionSkipped
          : false

      return {
        ...deposit,
        withdrawn: !!withdrawal,
        redemptionNeeded,
      }
    })
  },
)

// This is only helper selector, should not be used in components
const selectActiveDeposits = createSelector(
  selectDepositsWithWithdrawalStatus,
  (deposits) =>
    deposits.filter(
      (deposit) => !deposit.withdrawn || deposit.redemptionNeeded,
    ),
)

export const selectUnbridgedDeposits = createSelector(
  selectActiveDeposits,
  (deposits) => deposits.filter(({ depositBridged }) => !depositBridged),
)

export const selectWithdrawalById = createSelector(
  [(_, id): number | null => id, selectWithdrawals],
  (id, withdrawals) =>
    withdrawals?.find((withdrawal) => withdrawal.depositId === id) ?? null,
)

export const selectBitcoinDeposits = createSelector(
  selectDeposits,
  (deposits) => deposits.filter((deposit) => deposit.token === "BTC"),
)

export const selectBitcoinTokensDeposits = createSelector(
  selectDeposits,
  (deposits) =>
    deposits.filter(({ token }) =>
      // We also want to include crv-stBTC deposits in further calculations
      isTokenEligibleForUnlocks(token),
    ),
)

export const selectDepositLoaded = (state: RootState) => !!state.portal.deposits

export const selectDepositById = createSelector(
  [(_, id): string | null => id, selectDeposits],
  (id, deposits) => deposits.find((deposit) => deposit.id === id) ?? null,
)

export const selectDepositByDepositId = createSelector(
  [(_, depositId): number | null => depositId, selectDeposits],
  (depositId, deposits) =>
    deposits.find((deposit) => deposit.depositId === depositId) ?? null,
)

export const selectMostRecentDeposit = createSelector(
  selectDeposits,
  (deposits) => (deposits.length ? deposits[deposits.length - 1] : null),
)

export const selectLocksForDeposit = createSelector(
  [(_, id): string => id, selectDepositById],
  (_, deposit) => deposit?.locks ?? [],
)

export const selectCurrentLockForDeposit = createSelector(
  [(_, id): string => id, selectLocksForDeposit],
  (_, locks) => (locks.length ? locks[locks.length - 1] : null),
)

export const selectTotalDepositsAmount = createSelector(
  selectDeposits,
  (deposits) => getTotalDepositsAmount(deposits),
)

export const selectTotalBitcoinDepositsAmount = createSelector(
  selectBitcoinTokensDeposits,
  (deposits) => getTotalDepositsAmount(deposits),
)

export const selectTotalBitcoinDepositsAmountFriendlyAmount = createSelector(
  selectTotalBitcoinDepositsAmount,
  (totalBitcoinDeposit) =>
    bigIntToFloat(totalBitcoinDeposit, 18, FRIENDLY_DECIMALS_AMOUNT),
)

export const selectSuperstackEligibleDeposits = createSelector(
  selectDepositsWithWithdrawalStatus,
  (deposits) =>
    deposits.filter(
      (deposit) =>
        SUPERSTACK_TOKENS.includes(deposit.token) &&
        !deposit.withdrawn &&
        !!deposit.depositId,
    ),
)

export const selectTotalSuperstackEligibleDepositsAmount = createSelector(
  selectSuperstackEligibleDeposits,
  (deposits) => getTotalDepositsAmount(deposits),
)

export const selectDepositedSuperstackEligibleTokens = createSelector(
  selectSuperstackEligibleDeposits,
  (deposits) => deposits.map((deposit) => deposit.token),
)

export const selectSuperStackDeposits = createSelector(
  selectDepositsWithWithdrawalStatus,
  (deposits) =>
    deposits.filter(
      (deposit) =>
        STBTC_TOKENS.includes(deposit.token as keyof StbtcTokens) &&
        !deposit.withdrawn,
    ),
)

export const selectTotalMintedAmount = createSelector(
  selectDeposits,
  (deposits) =>
    deposits.length
      ? deposits
          .map((deposit) => BigInt(deposit.mintedAmount))
          .reduce((a, b) => a + b)
      : 0n,
)

export const selectMigratableDeposits = createSelector(
  selectDepositsWithWithdrawalStatus,
  (deposits) =>
    deposits.filter(
      (deposit) =>
        MIGRATABLE_TOKENS.includes(deposit.token) &&
        !deposit.withdrawn &&
        !!deposit.depositId &&
        !deposit.migration,
    ),
)

export const selectDepositsWithMigration = createSelector(
  selectDepositsWithWithdrawalStatus,
  (deposits) => deposits.filter((deposit) => !!deposit.migration),
)

export const selectMigratingDeposits = createSelector(
  selectDepositsWithMigration,
  (deposits) =>
    deposits.filter((deposit) => !deposit.migration?.returnTransactionId),
)

export const selectMigratedDeposits = createSelector(
  selectDepositsWithMigration,
  (deposits) =>
    deposits.filter((deposit) => !!deposit.migration?.returnTransactionId),
)

export const selectDepositedTokens = createSelector(
  selectActiveDeposits,
  (deposits) => [...new Set(deposits.map((deposit) => deposit.token))],
)

// Check for basic conditions to use the portal
export const selectCanUsePortal = createSelector(
  selectCanSendTransactions,
  selectIsConnected,
  selectIsOnEthereumChain,
  selectIsOnMezoChain,
  (canSendTransactions, isConnected, isOnEthereum, isOnMatsnet) =>
    canSendTransactions && isConnected && (isOnEthereum || isOnMatsnet),
)

export const selectPortalStatistics = (state: RootState) =>
  state.portal.statistics

export const selectHasLoadedTransactions = (state: RootState) =>
  state.portal.hasLoadedTransactions
